import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type {
    KeyCompetenciesData,
    KeyCompetenciesDescribeResult,
    KeyCompetencyItem,
    KeyCompetencyListType,
    KeyCompetencySelectMode,
} from '@/types/review/KeyCompetencies';
import type { LocalizedText } from '@/utils/localization';

import { type AiResultNotification } from '@/api/ai-gateway/aiHub';
import { managerAssessmentService } from '@/api/cycle-evaluation-services/manager-assessment';
import { useAiResult } from '@/hooks/ai/useAiResult';
import { showWarningToast } from '@/plugins/Toast';

const DEFAULT_ERROR = 'Failed to update the competencies.';
const AUTOSAVE_DELAY_MS = 600;
const EMPTY: KeyCompetenciesData = { mastered: [], growth: [] };

const listKey = (listType: KeyCompetencyListType): keyof KeyCompetenciesData =>
    listType === 'Mastered' ? 'mastered' : 'growth';

/**
 * Owns the Prepare Meeting Mastered/Growth lists: loads them (backend seeds from the summary), autosaves every
 * change (debounced), and drives "Regenerate with AI" as a two-step select (deterministic, code-side) →
 * describe (AI prose over SignalR) → fill → save. Both lists can be generated independently and concurrently.
 */
export function useKeyCompetencies(cycleInstanceId: string) {
    const { t } = useTranslation();
    const query = useQuery<KeyCompetenciesData | null, Error>({
        queryKey: ['manager-assessment-key-competencies', cycleInstanceId],
        queryFn: () => managerAssessmentService.getKeyCompetencies(cycleInstanceId),
        enabled: !!cycleInstanceId,
    });

    const [data, setData] = useState<KeyCompetenciesData | null>(null);
    const dataRef = useRef<KeyCompetenciesData | null>(null);
    const saveTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

    const [generatingList, setGeneratingList] = useState<Record<KeyCompetencyListType, boolean>>({
        Mastered: false,
        Growth: false,
    });
    const [reserveExhausted, setReserveExhausted] = useState<
        Record<KeyCompetencyListType, boolean>
    >({
        Mastered: false,
        Growth: false,
    });
    const [error, setError] = useState<string | null>(null);

    const [correlationIds, setCorrelationIds] = useState<
        Record<KeyCompetencyListType, string | null>
    >({
        Mastered: null,
        Growth: null,
    });
    const describeContext = useRef<
        Record<
            KeyCompetencyListType,
            {
                competencyIds: string[];
                applied: KeyCompetencyItem[];
                mode: KeyCompetencySelectMode;
            } | null
        >
    >({
        Mastered: null,
        Growth: null,
    });

    const masteredResult = useAiResult(correlationIds.Mastered);
    const growthResult = useAiResult(correlationIds.Growth);

    // Hydrate the editable state from the query (initial load / cycle change). Autosave writes back, so the
    // query is not invalidated afterwards.
    useEffect(() => {
        if (query.data !== undefined) {
            dataRef.current = query.data;
            setData(query.data);
        }
    }, [query.data]);

    useEffect(() => () => clearTimeout(saveTimer.current), []);

    const save = useCallback(
        (next: KeyCompetenciesData) => {
            clearTimeout(saveTimer.current);
            saveTimer.current = setTimeout(() => {
                void managerAssessmentService
                    .saveKeyCompetencies(cycleInstanceId, next)
                    .catch(() => {
                        setError(DEFAULT_ERROR);
                    });
            }, AUTOSAVE_DELAY_MS);
        },
        [cycleInstanceId],
    );

    // Applies a change to the in-memory lists and schedules an autosave.
    const commit = useCallback(
        (next: KeyCompetenciesData) => {
            dataRef.current = next;
            setData(next);
            save(next);
        },
        [save],
    );

    const mutateList = useCallback(
        (
            listType: KeyCompetencyListType,
            updater: (items: KeyCompetencyItem[]) => KeyCompetencyItem[],
        ) => {
            const current = dataRef.current ?? EMPTY;
            commit({ ...current, [listKey(listType)]: updater(current[listKey(listType)]) });
        },
        [commit],
    );

    const addItem = useCallback(
        (listType: KeyCompetencyListType, item: KeyCompetencyItem) => {
            // A competency can only sit on one list: ignore an add when it is already on either list.
            const lists = dataRef.current ?? EMPTY;
            if (
                item.competencyId &&
                [...lists.mastered, ...lists.growth].some(
                    (i) => i.competencyId === item.competencyId,
                )
            ) {
                return;
            }
            mutateList(listType, (items) => [...items, item]);
        },
        [mutateList],
    );

    const updateItem = useCallback(
        (listType: KeyCompetencyListType, index: number, patch: Partial<KeyCompetencyItem>) =>
            mutateList(listType, (items) =>
                items.map((it, i) => (i === index ? { ...it, ...patch } : it)),
            ),
        [mutateList],
    );

    const removeItem = useCallback(
        (listType: KeyCompetencyListType, index: number) =>
            mutateList(listType, (items) => items.filter((_, i) => i !== index)),
        [mutateList],
    );

    const regenerate = useCallback(
        async (listType: KeyCompetencyListType, mode: KeyCompetencySelectMode) => {
            if (!cycleInstanceId) return;
            // Exclude BOTH lists: a competency can only sit on one list at a time.
            const lists = dataRef.current ?? EMPTY;
            const excludeCompetencyIds = [...lists.mastered, ...lists.growth]
                .map((i) => i.competencyId)
                .filter((id): id is string => Boolean(id));

            setError(null);
            setGeneratingList((prev) => ({ ...prev, [listType]: true }));
            try {
                const selection = await managerAssessmentService.selectKeyCompetencies(
                    cycleInstanceId,
                    {
                        listType,
                        mode,
                        excludeCompetencyIds,
                    },
                );
                setReserveExhausted((prev) => ({
                    ...prev,
                    [listType]: selection.reserveExhausted,
                }));

                // Newly selected competencies; descriptions will be written by the AI before they are shown.
                const applied: KeyCompetencyItem[] = selection.items.map((s) => ({
                    competencyId: s.competencyId,
                    name: s.name,
                    description: {},
                    source: 'selection',
                }));

                if (applied.length === 0) {
                    showWarningToast({
                        message: t(
                            mode === 'Replace'
                                ? 'postReviewPrepareMeeting.competencies.replaceListedExhausted'
                                : 'postReviewPrepareMeeting.competencies.addThreeExhausted',
                        ),
                    });
                    setGeneratingList((prev) => ({ ...prev, [listType]: false }));
                    return;
                }

                // Ask the AI to describe exactly the applied competencies, in order (it echoes each by number).
                // The list is only updated once the descriptions come back, so the user sees names + prose together.
                const competencyIds = applied
                    .map((i) => i.competencyId)
                    .filter((id): id is string => Boolean(id));
                describeContext.current = {
                    ...describeContext.current,
                    [listType]: { competencyIds, applied, mode },
                };
                const id = crypto.randomUUID();
                setCorrelationIds((prev) => ({ ...prev, [listType]: id }));
                await managerAssessmentService.describeKeyCompetencies({
                    cycleInstanceId,
                    correlationId: id,
                    listType,
                    competencyIds,
                });
            } catch {
                setError(DEFAULT_ERROR);
                setGeneratingList((prev) => ({ ...prev, [listType]: false }));
            }
        },
        [cycleInstanceId, t],
    );

    const applyResult = useCallback(
        (
            listType: KeyCompetencyListType,
            result: AiResultNotification,
            expectedCorrelationId: string,
        ) => {
            if (result.correlationId !== expectedCorrelationId) return;

            if (!result.success) {
                setError(result.error ?? DEFAULT_ERROR);
                setGeneratingList((prev) => ({ ...prev, [listType]: false }));
                return;
            }

            const context = describeContext.current[listType];
            if (!context) return;

            try {
                const parsed = JSON.parse(result.content ?? '') as KeyCompetenciesDescribeResult;
                const descriptionByNumber = new Map<number, LocalizedText>();
                for (const item of parsed.items ?? []) {
                    descriptionByNumber.set(item.number, item.description);
                }

                const appliedWithDescriptions: KeyCompetencyItem[] = context.applied.map(
                    (item, index) => ({
                        ...item,
                        description: descriptionByNumber.get(index + 1) ?? {},
                    }),
                );

                const current = dataRef.current ?? EMPTY;
                const key = listKey(listType);
                const currentList = current[key];

                // The other list may have changed while the AI was writing: never apply a competency it holds.
                const otherKey = listKey(listType === 'Mastered' ? 'Growth' : 'Mastered');
                const otherIds = new Set(current[otherKey].map((i) => i.competencyId));
                const fresh = appliedWithDescriptions.filter(
                    (i) => !i.competencyId || !otherIds.has(i.competencyId),
                );

                let nextList: KeyCompetencyItem[];
                if (context.mode === 'Replace') {
                    nextList = fresh;
                } else {
                    const existingIds = new Set(currentList.map((i) => i.competencyId));
                    nextList = [
                        ...currentList,
                        ...fresh.filter((i) => !existingIds.has(i.competencyId)),
                    ];
                }

                commit({ ...current, [key]: nextList });
            } catch {
                setError(DEFAULT_ERROR);
            } finally {
                setGeneratingList((prev) => ({ ...prev, [listType]: false }));
            }
        },
        [commit],
    );

    useEffect(() => {
        if (!masteredResult || !correlationIds.Mastered) return;
        applyResult('Mastered', masteredResult, correlationIds.Mastered);
    }, [masteredResult, correlationIds.Mastered, applyResult]);

    useEffect(() => {
        if (!growthResult || !correlationIds.Growth) return;
        applyResult('Growth', growthResult, correlationIds.Growth);
    }, [growthResult, correlationIds.Growth, applyResult]);

    return {
        mastered: data?.mastered ?? [],
        growth: data?.growth ?? [],
        /** True while the cycle's lists are loading for the first time. */
        isLoading: query.isLoading,
        /** False when the cycle has no score to select from (nothing to show). */
        hasData: data !== null,
        generatingList,
        reserveExhausted,
        error,
        regenerate,
        addItem,
        updateItem,
        removeItem,
    };
}
